import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { getTournaments } from '../../services/tournament.service';
import { Box, IconButton, Tooltip } from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
// import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useUser } from '../../context/UserContext';
import { Permissions } from '../../context/permissions';
import { TournamentStatus } from './tournament-constants';

const TournamentTable = () => {
  const { userProfile } = useUser();
  const navigate = useNavigate();

  // data and fetching state
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  // table state
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!data.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const result = await getTournaments(pagination.pageIndex, pagination.pageSize);

        setData(result.data);
        setRowCount(result.totalRowCount);
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // columnFilters,
    // globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    // sorting,
  ]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'fecha_inicio',
        header: 'Fecha',
        size: 100,
      },
      {
        accessorKey: 'tor_nombre',
        header: 'Torneo',
        size: 240,
      },
      {
        accessorKey: 'tor_tipo_detalle',
        header: 'Tipo',
        size: 100,
      },
      {
        accessorKey: 'tor_sistema_detalle',
        header: 'Sistema',
        size: 100,
      },
      {
        accessorKey: 'tor_ritmo_detalle',
        header: 'Ritmo',
        size: 100,
      },
      {
        accessorKey: 'tor_estado_detalle',
        header: 'Estado',
        size: 120,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    getRowId: (row) => row.tor_id,
    initialState: { showColumnFilters: false, density: 'compact' },
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    muiToolbarAlertBannerProps: isError
      ? {
          color: 'error',
          children: 'Error cargando datos',
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    muiTableHeadCellProps: {
      sx: {
        background: '#565656',
        color: 'black',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        background: '#9295a9',
        color: 'black',
      },
    },
    renderRowActions: ({ row }) => (
      <Box>
        {userProfile?.permissions?.includes(Permissions.MEMBER_ACTIONS) &&
          row.original.tor_estado === TournamentStatus.OPEN_FOR_INSCRIPTIONS && (
            <Tooltip title='Inscribirse'>
              <IconButton style={{ color: '#0645a7' }} onClick={() => console.info('Inscribir')}>
                <PersonAddIcon />
              </IconButton>
            </Tooltip>
          )}

        {row.original.tor_estado === TournamentStatus.FINISHED && (
          <Tooltip title='Ver'>
            <IconButton style={{ color: '#0645a7' }} onClick={() => navigate(`/tournaments/${row.original.tor_id}`)}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}

        {userProfile?.permissions?.includes(Permissions.MANAGE_TOURNAMENTS) && (
          <Tooltip title='Editar'>
            <IconButton style={{ color: '#0645a7' }} onClick={() => console.info('Edit')}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}

        {userProfile?.permissions?.includes(Permissions.MANAGE_TOURNAMENTS) && (
          <Tooltip title='Eliminar'>
            <IconButton style={{ color: '#780303' }} onClick={() => console.info('Delete')}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Acciones',
      },
    },
  });

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default TournamentTable;
